import { Marker, Popup } from 'react-leaflet';
import type { PopupEvent } from 'leaflet';
import type { ICallDetails } from '../../../../../../../../../models/call-details.model';
import type { ILineDetails } from '../../../../../../../../../models/line-details.model';
import { ButtonDivIcon } from '../../../../../../../../Shared/Map/markers/button-div-icon';
import { TranslationService } from '../../../../../../../../../services/translation.service';
import { MarkerHelper } from '../../../../../../../../../helpers/marker.helper';

function getIconSVG(backgroundColor: string, foregroundColor: string, opacity: number) {
  const circleMarkerSvg = MarkerHelper.getCircleMarkerSvg(6, 3, foregroundColor, backgroundColor, opacity);
  return {
    className: 'button-div-icon call-marker',
    svg: circleMarkerSvg.svg,
    w: circleMarkerSvg.width,
    h: circleMarkerSvg.height,
  };
}

function CallMarker({ call, line, iconOpacity = 1 }: { call: ICallDetails; line: ILineDetails; iconOpacity?: number }) {
  if (!call.latitude || !call.longitude) {
    return null;
  }

  const iconSvg = getIconSVG(line.backgroundColor, line.foregroundColor, iconOpacity);

  const icon = new ButtonDivIcon(
    {
      className: iconSvg.className,
      iconSize: [iconSvg.w, iconSvg.h],
      iconAnchor: [9, 14], // Empirically based on the icon width and height
      html: iconSvg.svg,
    },
    'Angöring'
  );

  function handleOnPopupOpen(popupEvent: PopupEvent) {
    // Inspired by http://melmo.github.io/accessibility/code/dist/leaflet.html
    // Focus on the opened popup
    const stopAreaPopups = popupEvent.popup.getElement()?.getElementsByClassName('call-popup');
    if (stopAreaPopups && stopAreaPopups.length > 0) {
      const stopAreaPopup = stopAreaPopups[0] as HTMLElement;
      stopAreaPopup.setAttribute('tabindex', '-1');
      stopAreaPopup.focus();
    }

    // Fix the a11y attributes on the close button
    const closeButtons = popupEvent.popup.getElement()?.getElementsByClassName('leaflet-popup-close-button');
    if (closeButtons && closeButtons.length > 0) {
      const closeButton = closeButtons[0] as HTMLLinkElement;
      closeButton.setAttribute('role', 'button');
      closeButton.setAttribute('aria-label', TranslationService.get('common.map.closepopup'));
      closeButton.href = '#';
    }
  }

  return (
    <Marker icon={icon} position={[call.latitude, call.longitude]} eventHandlers={{ popupopen: (e) => handleOnPopupOpen(e) }}>
      <Popup>
        <div className="call-popup">
          <span>
            {call.stopPoint.name} {TranslationService.get('common.platform').toLocaleLowerCase()} {call.plannedPlatform}
          </span>
        </div>
      </Popup>
    </Marker>
  );
}

export default CallMarker;
